import { useRef, useState } from 'react';
import classNames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
// Import Helpers
import { genericMemo } from 'services/Helpers';
// Import Components
import { Icon } from '@geneui/components';
// Import Styles
import styles from '../draggableList.module.scss';

/**
 * Enum for drag types used in react-dnd
 */
enum DragTypesEnum {
    /** Row drag type */
    Row = 'Row',
}

/**
 * Props for DraggableRow component
 * @template R - Row data type
 */
interface Props<R extends AnyObject> {
    /** Content to render inside the row */
    content: React.ReactNode;
    /** Whether the row is draggable */
    isDraggable: boolean;
    rowContainerClassName?: string;
    rowContentClassName?: string;
    /** Returns the row data for dragging */
    getDraggingRow: () => R;
    handleRowDrop: (item: R, target: R) => void;
}

/**
 * DraggableRow component for rendering a draggable row in a DnD list.
 * Integrates with react-dnd for drag-and-drop functionality.
 *
 * @template R - Generic Row data type which should be custom row data object
 * @param {Props<R>} props - DraggableRow props
 * @returns {JSX.Element} DraggableRow component
 */
const DraggableRow = <R extends AnyObject>(props: Props<R>) => {
    const { content, isDraggable, rowContainerClassName, rowContentClassName, handleRowDrop, getDraggingRow } = props;

    /** Ref for the row DOM element */
    const rowItemRef = useRef<HTMLDivElement | null>(null);

    /** State for row width and height (used for drag preview) */
    const [itemClientWidth, setItemClientWidth] = useState(rowItemRef.current?.clientWidth ?? 0);
    const [itemClientHeight, setItemClientHeight] = useState(rowItemRef.current?.clientHeight ?? 0);

    /**
     * Setup drag source using react-dnd
     */
    const [, drag, preview] = useDrag<
        R & { style: { width: number; height: number } },
        unknown,
        { isDragging: boolean }
    >({
        item: {
            ...getDraggingRow(),
            style: { width: itemClientWidth, height: itemClientHeight },
        },
        type: DragTypesEnum.Row,
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    });

    /**
     * Setup drop target using react-dnd
     */
    const [, drop] = useDrop<R, void, { isOver: boolean }>({
        accept: DragTypesEnum.Row,
        drop: (item, _monitor) => handleRowDrop(item, getDraggingRow()),
        collect: (monitor) => ({ isOver: !!monitor.isOver() }),
    });

    /**
     * Set up drag preview image
     */
    preview(getEmptyImage(), { captureDraggingState: true });

    return (
        <div
            ref={(current) => {
                rowItemRef.current = current;
                setItemClientWidth(rowItemRef.current?.clientWidth ?? 0);
                setItemClientHeight(rowItemRef.current?.clientHeight ?? 0);
                if (isDraggable) {
                    drag(current);
                    drop(current);
                }
            }}
            className={classNames(styles.DND_Row, { [styles.draggable]: isDraggable }, rowContainerClassName)}
        >
            <div className={classNames(styles.DND_RowContent, rowContentClassName)}>{content}</div>

            {isDraggable && <Icon type="bc-icon-drag" />}
        </div>
    );
};

export default genericMemo(DraggableRow);
